import React, {FunctionComponent, useState, useRef, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import useSWR from 'swr';
import {useGoogleMap} from '@ubilabs/google-maps-react-hooks';
import cx from 'classnames';

import {Work} from '../../../../../shared/types/work';

import {
  MAX_NUMBER_OF_SUGGESTIONS,
  MAX_NUMBER_OF_WORK_SUGGESTIONS
} from '../../../constants/search';

import a11yClick from '../../libs/a11y-click';
import getWorkSuggestions from '../../libs/get-work-suggestions';

import LocationIcon, {LocationIconVariant} from '../icons/location/location';

import styles from './search-suggestions.module.css';

export interface SearchSuggestion {
  id: string;
  label: string;
  isWorkOfArt?: boolean;
  isBeforeYear1945?: boolean;
}

interface Props {
  className?: string;
  inputValue: string;
  onSelect: (suggestion: SearchSuggestion) => void;
  activeSuggestionIndex: number | null;
}

const SearchSuggestions: FunctionComponent<Props> = props => {
  const {className, inputValue, onSelect, activeSuggestionIndex} = props;
  const history = useHistory();
  const {map} = useGoogleMap();

  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);

  const [suggestions, setSuggestions] = useState<Array<SearchSuggestion>>([]);

  const {data: works} = useSWR<Array<Work>>('works');

  // Initialize Autocomplete and Places Service
  useEffect(() => {
    if (!map) {
      return;
    }

    autocompleteService.current = new google.maps.places.AutocompleteService();
    placesService.current = new google.maps.places.PlacesService(map);
  }, [map]);

  // Get suggestions from Autocomplete Service
  const getAutocompleteSuggestions = async (
    input: string,
    maxNumberOfSuggestions: number
  ): Promise<Array<SearchSuggestion>> =>
    new Promise(resolve => {
      if (!autocompleteService.current) {
        resolve([]);
        return;
      }

      autocompleteService.current.getPlacePredictions(
        {
          input // TODO: add bounds restriction
        },
        (
          predictions: google.maps.places.AutocompletePrediction[] | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          if (
            status !== google.maps.places.PlacesServiceStatus.OK ||
            !predictions
          ) {
            resolve([]);
            return;
          }

          const autocompleteSuggestions = predictions
            .slice(0, maxNumberOfSuggestions)
            .map(prediction => ({
              id: prediction.place_id,
              label: prediction.description
            }));

          resolve(autocompleteSuggestions);
        }
      );
    });

  // Update suggestions
  // Query work of arts and get autocomplete place suggestions
  useEffect(() => {
    const getSuggestions = async () => {
      try {
        const workSuggestions = getWorkSuggestions(
          works,
          inputValue,
          MAX_NUMBER_OF_WORK_SUGGESTIONS
        );
        const autocompleteSuggestions = await getAutocompleteSuggestions(
          inputValue,
          MAX_NUMBER_OF_SUGGESTIONS - workSuggestions.length
        );

        setSuggestions([...workSuggestions, ...autocompleteSuggestions]);
      } catch (error) {
        console.error(error);
      }
    };

    if (inputValue.length >= 3) {
      getSuggestions();
    } else {
      setSuggestions([]);
    }
  }, [inputValue]);

  // Handle suggestion selection
  const selectSuggestion = (
    event: React.MouseEvent | React.KeyboardEvent<HTMLLIElement>,
    suggestion: SearchSuggestion
  ) => {
    if (!a11yClick(event)) {
      return;
    }

    onSelect(suggestion);

    // If the selected place is a work of art, go to the details view ...
    if (suggestion.isWorkOfArt) {
      history.push(`/works/${suggestion.id}`);
      return;
    }

    // ... otherwise get the location of the selected place and zoom to it
    placesService.current?.getDetails(
      {placeId: suggestion.id},
      (
        placeResult: google.maps.places.PlaceResult | null,
        status: google.maps.places.PlacesServiceStatus
      ) => {
        if (
          status !== google.maps.places.PlacesServiceStatus.OK ||
          !placeResult
        ) {
          return;
        }

        const position = placeResult.geometry?.location;

        if (map && position) {
          map.setZoom(14);
          map.panTo(position);
        }
      }
    );
  };

  if (!suggestions.length) {
    return null;
  }

  return (
    <ul
      className={cx(className, styles.suggestions)}
      id="search-suggestions"
      role="listbox">
      {suggestions.map((suggestion, index) => (
        <li
          key={suggestion.id}
          onClick={event => selectSuggestion(event, suggestion)}
          onKeyDown={event => selectSuggestion(event, suggestion)}
          id={`search-suggestion-${index}`}
          role="option"
          aria-selected={index === activeSuggestionIndex}
          tabIndex={-1}>
          <LocationIcon
            className={styles.icon}
            variant={
              (suggestion.isBeforeYear1945 === true &&
                LocationIconVariant.YELLOW) ||
              (suggestion.isBeforeYear1945 === false &&
                LocationIconVariant.BLUE) ||
              LocationIconVariant.DEFAULT
            }
          />
          <span>{suggestion.label}</span>
        </li>
      ))}
    </ul>
  );
};

export default SearchSuggestions;
